/* eslint-disable react/jsx-max-depth */
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { ChevronDown } from "@/components/icons"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { RawJsonTab } from "./shared/raw-json-tab"
import { Button } from "../../../components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Switch } from "../../../components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"

interface DynamicTitlesConfig {
  topic_shift_enabled: boolean
  topic_shift_min_messages: number
  topic_shift_check_interval: number
  auto_accept_first_improvement: boolean
}

export type { DynamicTitlesConfig }

interface EditDynamicTitlesDialogProps {
  isOpen: boolean
  config: DynamicTitlesConfig
  onSave: (
    config: DynamicTitlesConfig,
    options?: { reason?: string; source?: "form" | "raw" }
  ) => Promise<void>
  onOpenChange: (open: boolean) => void
}

const RAW_ENDPOINT = "/admin/features/dynamic_titles/raw"

export function EditDynamicTitlesDialog({
  isOpen,
  config,
  onSave,
  onOpenChange,
}: EditDynamicTitlesDialogProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  // Raw-tab routes through the raw endpoint (with hash + reason) instead
  // of the regular feature config PUT — otherwise two admins editing the
  // same dialog can silently overwrite each other, and the audit trail
  // misses the "raw" source tag.
  const { data: rawData } = useRawEntityQuery<DynamicTitlesConfig>(RAW_ENDPOINT, isOpen)
  const rawSaveMutation = useSaveRawEntityMutation<DynamicTitlesConfig>({
    endpoint: RAW_ENDPOINT,
    invalidateOnSuccess: [["admin", "features"]],
  })
  const rawEntity = (rawData?.entity ?? config) as DynamicTitlesConfig
  const rawHash = rawData?.hash ?? ""

  // Form state
  const [topicShiftEnabled, setTopicShiftEnabled] = useState(true)
  const [minMessages, setMinMessages] = useState(10)
  const [checkInterval, setCheckInterval] = useState(5)
  const [autoAccept, setAutoAccept] = useState(true)

  // Collapsible state
  const [topicShiftOpen, setTopicShiftOpen] = useState(false)
  const [behaviorOpen, setBehaviorOpen] = useState(false)

  // Initialize form from config
  useEffect(() => {
    setTopicShiftEnabled(config.topic_shift_enabled)
    setMinMessages(config.topic_shift_min_messages)
    setCheckInterval(config.topic_shift_check_interval)
    setAutoAccept(config.auto_accept_first_improvement)
  }, [config])

  const handleSubmit = async () => {
    if (minMessages < 1) {
      toast.error("Minimum messages must be at least 1")
      return
    }

    if (checkInterval < 1) {
      toast.error("Check interval must be at least 1")
      return
    }

    const newConfig: DynamicTitlesConfig = {
      topic_shift_enabled: topicShiftEnabled,
      topic_shift_min_messages: minMessages,
      topic_shift_check_interval: checkInterval,
      auto_accept_first_improvement: autoAccept,
    }

    setSaving(true)
    try {
      await onSave(newConfig)
      toast.success(t("adminDashboard.features.dynamicTitles.success"))
      onOpenChange(false)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.dynamicTitles.error") +
          (error instanceof Error ? `: ${error.message}` : "")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("adminDashboard.features.dynamicTitles.configTitle")}</DialogTitle>
          <DialogDescription>
            {t("adminDashboard.features.dynamicTitles.configDescription")}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.features.dynamicTitles.formTab", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.features.dynamicTitles.rawTab", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="form">
            <div className="space-y-6 py-4">
              {/* Topic Shift Detection */}
              <Collapsible open={topicShiftOpen} onOpenChange={setTopicShiftOpen}>
                <CollapsibleTrigger asChild>
                  <Button className="w-full justify-between pr-0 pl-1" variant="ghost">
                    <span className="font-medium">
                      {t("adminDashboard.features.dynamicTitles.topicShiftSection")}
                    </span>
                    <ChevronDown
                      className={`size-4 transition-transform ${topicShiftOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="border-muted ml-2 space-y-4 border-l-2 pt-4 pl-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="topic-shift-enabled">
                        {t("adminDashboard.features.dynamicTitles.topicShiftEnabled")}
                      </Label>
                    </div>
                    <Switch
                      checked={topicShiftEnabled}
                      id="topic-shift-enabled"
                      onCheckedChange={setTopicShiftEnabled}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="min-messages">
                      {t("adminDashboard.features.dynamicTitles.minMessages")}
                    </Label>
                    <Input
                      id="min-messages"
                      min="1"
                      type="number"
                      value={minMessages}
                      onChange={(e) => setMinMessages(parseInt(e.target.value, 10) || 1)}
                    />
                    <p className="text-muted-foreground text-sm">
                      {t("adminDashboard.features.dynamicTitles.minMessagesHelp")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="check-interval">
                      {t("adminDashboard.features.dynamicTitles.checkInterval")}
                    </Label>
                    <Input
                      id="check-interval"
                      min="1"
                      type="number"
                      value={checkInterval}
                      onChange={(e) => setCheckInterval(parseInt(e.target.value, 10) || 1)}
                    />
                    <p className="text-muted-foreground text-sm">
                      {t("adminDashboard.features.dynamicTitles.checkIntervalHelp")}
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Behavior */}
              <Collapsible open={behaviorOpen} onOpenChange={setBehaviorOpen}>
                <CollapsibleTrigger asChild>
                  <Button className="w-full justify-between pr-0 pl-1" variant="ghost">
                    <span className="font-medium">
                      {t("adminDashboard.features.dynamicTitles.behaviorSection")}
                    </span>
                    <ChevronDown
                      className={`size-4 transition-transform ${behaviorOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="border-muted ml-2 space-y-4 border-l-2 pt-4 pl-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="auto-accept">
                        {t("adminDashboard.features.dynamicTitles.autoAccept")}
                      </Label>
                      <p className="text-muted-foreground text-sm">
                        {t("adminDashboard.features.dynamicTitles.autoAcceptDescription")}
                      </p>
                    </div>
                    <Switch checked={autoAccept} id="auto-accept" onCheckedChange={setAutoAccept} />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <DialogFooter>
              <Button disabled={saving} variant="outline" onClick={() => onOpenChange(false)}>
                {t("adminDashboard.features.dynamicTitles.cancel")}
              </Button>
              <Button disabled={saving} onClick={handleSubmit}>
                {saving
                  ? t("adminDashboard.features.dynamicTitles.saving")
                  : t("adminDashboard.features.dynamicTitles.save")}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="raw">
            <RawJsonTab
              confirmToken="dynamic_titles"
              isPending={saving || rawSaveMutation.isPending || !rawHash}
              value={rawEntity}
              resourceLabel={t("adminDashboard.features.dynamicTitles.rawResourceLabel", {
                defaultValue: "dynamic titles config",
              })}
              onSave={async (next, reason) => {
                // Raw edits go through the raw entity endpoint (hash-based
                // optimistic concurrency + reason-tagged audit), not the
                // regular feature config PUT. Form-tab saves still flow
                // through the prop-based ``onSave`` so the rest of the
                // page (optimistic cache updates, refetches) stays
                // unchanged for the common case.
                setSaving(true)
                try {
                  await rawSaveMutation.mutateAsync({
                    entity: next as Record<string, unknown>,
                    expectedHash: rawHash,
                    reason,
                    source: "raw",
                  })
                  toast.success(t("adminDashboard.features.dynamicTitles.success"))
                  onOpenChange(false)
                } catch (error) {
                  toast.error(
                    t("adminDashboard.features.dynamicTitles.error") +
                      (error instanceof Error ? `: ${error.message}` : "")
                  )
                  throw error
                } finally {
                  setSaving(false)
                }
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
